import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { OwnerLayout } from '../../components/layouts/OwnerLayout';
import { 
  FileText, Plus, Search, Filter, MoreHorizontal, Send, Check, X, 
  FileOutput, Trash2, Edit2, Eye, ArrowRight, Clock, AlertTriangle, Download
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';

const statusConfig = {
  draft: { label: 'Borrador', color: 'bg-slate-100 text-slate-700' },
  sent: { label: 'Enviado', color: 'bg-blue-100 text-blue-700' },
  accepted: { label: 'Aceptado', color: 'bg-green-100 text-green-700' },
  rejected: { label: 'Rechazado', color: 'bg-red-100 text-red-700' },
  executed: { label: 'Ejecutado', color: 'bg-purple-100 text-purple-700' },
};

const Quotes = () => {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [quotes, setQuotes] = useState([]);
  const [deliveryNotes, setDeliveryNotes] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [incidentNotifications, setIncidentNotifications] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [deleteDialog, setDeleteDialog] = useState({ open: false, item: null, type: null });
  const [activeTab, setActiveTab] = useState('quotes');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [quotesRes, deliveryRes, invoicesRes, notificationsRes] = await Promise.all([
        api.get('/quotes'),
        api.get('/delivery-notes'),
        api.get('/invoices'),
        api.get('/incident-notifications')
      ]);
      setQuotes(quotesRes.data);
      setDeliveryNotes(deliveryRes.data);
      setInvoices(invoicesRes.data);
      setIncidentNotifications(notificationsRes.data.filter(n => !n.quote_id));
    } catch (error) {
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleDelete = async () => {
    if (!deleteDialog.item) return;
    try {
      const endpoints = {
        quote: '/quotes',
        delivery: '/delivery-notes',
        invoice: '/invoices'
      };
      await api.delete(`${endpoints[deleteDialog.type]}/${deleteDialog.item.id}`);
      toast.success('Eliminado correctamente');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar');
    } finally {
      setDeleteDialog({ open: false, item: null, type: null });
    }
  };

  const handleStatusChange = async (quoteId, newStatus) => {
    try {
      if (newStatus === 'accepted') {
        await api.post(`/quotes/${quoteId}/accept`);
      } else if (newStatus === 'rejected') {
        await api.post(`/quotes/${quoteId}/reject`);
      } else if (newStatus === 'sent') {
        await api.post(`/quotes/${quoteId}/send`);
      }
      toast.success('Estado actualizado');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al actualizar estado');
    }
  };

  const handleConvert = async (id, type, target) => {
    try {
      if (type === 'quote' && target === 'delivery') {
        await api.post(`/quotes/${id}/to-delivery-note`);
        toast.success('Albarán creado correctamente');
      } else if (type === 'quote' && target === 'invoice') {
        await api.post(`/quotes/${id}/to-invoice`);
        toast.success('Factura creada correctamente');
      } else if (type === 'delivery' && target === 'invoice') {
        await api.post(`/delivery-notes/${id}/to-invoice`);
        toast.success('Factura creada correctamente');
      }
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al convertir');
    }
  };

  const handleCreateQuoteFromIncident = async (notificationId) => {
    try {
      const res = await api.post(`/incident-notifications/${notificationId}/create-quote`);
      toast.success('Presupuesto creado');
      navigate(`/quotes/${res.data.id}/edit`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear presupuesto');
    }
  };

  const filterItems = (items) => {
    return items.filter(item => {
      const matchesSearch = 
        item.client_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.quote_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.delivery_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.invoice_number?.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesStatus = statusFilter === 'all' || item.status === statusFilter;
      return matchesSearch && matchesStatus;
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR' }).format(amount || 0);
  };

  const downloadPdf = async (id, type) => {
    const loadingToast = toast.loading('Generando PDF...');
    try {
      const token = localStorage.getItem('token');
      const endpoints = {
        quote: 'quotes',
        'delivery-note': 'delivery-notes',
        invoice: 'invoices'
      };
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/${endpoints[type]}/${id}/pdf`;
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      
      if (!res.ok) {
        throw new Error('Error al generar PDF');
      }
      
      const blob = await res.blob();
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `documento_${id.slice(0, 8)}.pdf`;
      link.click();
      
      toast.dismiss(loadingToast);
      toast.success('PDF descargado correctamente');
    } catch (error) {
      toast.dismiss(loadingToast);
      toast.error('Error al descargar PDF');
    }
  };

  const DocumentRow = ({ item, type }) => {
    const number = item.quote_number || item.delivery_number || item.invoice_number;
    const status = statusConfig[item.status] || { label: item.status, color: 'bg-slate-100 text-slate-700' };
    
    return (
      <div className="flex items-center justify-between p-4 bg-white border rounded-lg hover:shadow-sm transition-shadow">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3 mb-1">
            <span className="font-mono text-sm font-medium text-sky-600">{number}</span>
            <Badge className={status.color}>{status.label}</Badge>
          </div>
          <p className="font-medium truncate">{item.client_name}</p>
          <p className="text-sm text-slate-500">
            {item.created_at && format(new Date(item.created_at), "d MMM yyyy", { locale: es })}
          </p>
        </div>
        <div className="flex items-center gap-4">
          <div className="text-right">
            <p className="font-bold text-lg">{formatCurrency(item.total)}</p>
            <p className="text-xs text-slate-500">IVA incl.</p>
          </div>
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => downloadPdf(item.id, type)}
            title="Descargar PDF"
          >
            <Download className="w-4 h-4" />
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm">
                <MoreHorizontal className="w-4 h-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => downloadPdf(item.id, type)}>
                <Download className="w-4 h-4 mr-2" />
                Descargar PDF
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate(`/${type}s/${item.id}`)}>
                <Eye className="w-4 h-4 mr-2" />
                Ver detalles
              </DropdownMenuItem>
              {item.status !== 'executed' && item.status !== 'invoiced' && (
                <DropdownMenuItem onClick={() => navigate(`/${type}s/${item.id}/edit`)}>
                  <Edit2 className="w-4 h-4 mr-2" />
                  Editar
                </DropdownMenuItem>
              )}
              <DropdownMenuSeparator />
              {type === 'quote' && item.status === 'draft' && (
                <DropdownMenuItem onClick={() => handleStatusChange(item.id, 'sent')}>
                  <Send className="w-4 h-4 mr-2" />
                  Enviar al cliente
                </DropdownMenuItem>
              )}
              {type === 'quote' && item.status === 'sent' && (
                <>
                  <DropdownMenuItem onClick={() => handleStatusChange(item.id, 'accepted')}>
                    <Check className="w-4 h-4 mr-2" />
                    Marcar como aceptado
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => handleStatusChange(item.id, 'rejected')}>
                    <X className="w-4 h-4 mr-2" />
                    Marcar como rechazado
                  </DropdownMenuItem>
                </>
              )}
              {type === 'quote' && item.status === 'accepted' && (
                <>
                  <DropdownMenuItem onClick={() => handleConvert(item.id, 'quote', 'delivery')}>
                    <FileOutput className="w-4 h-4 mr-2" />
                    Crear albarán
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => handleConvert(item.id, 'quote', 'invoice')}>
                    <FileText className="w-4 h-4 mr-2" />
                    Crear factura directa
                  </DropdownMenuItem>
                </>
              )}
              {type === 'delivery-note' && item.status !== 'invoiced' && (
                <DropdownMenuItem onClick={() => handleConvert(item.id, 'delivery', 'invoice')}>
                  <ArrowRight className="w-4 h-4 mr-2" />
                  Convertir a factura
                </DropdownMenuItem>
              )}
              <DropdownMenuSeparator />
              {item.status !== 'executed' && item.status !== 'invoiced' && (
                <DropdownMenuItem 
                  className="text-red-600"
                  onClick={() => setDeleteDialog({ open: true, item, type })}
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Eliminar
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    );
  };

  const unreadNotifications = incidentNotifications.filter(n => !n.is_read).length;

  return (
    <OwnerLayout>
      <div className="space-y-6" data-testid="quotes-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="font-display text-2xl font-bold text-slate-900">Facturación</h1>
            <p className="text-slate-500">Presupuestos, albaranes y facturas</p>
          </div>
          <Button onClick={() => navigate('/quotes/new')} className="bg-sky-600 hover:bg-sky-700">
            <Plus className="w-4 h-4 mr-2" />
            Nuevo Presupuesto
          </Button>
        </div>

        {/* Incident Notifications Alert */}
        {unreadNotifications > 0 && (
          <Card className="border-amber-300 bg-amber-50">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <AlertTriangle className="w-5 h-5 text-amber-600" />
                <div className="flex-1">
                  <p className="font-medium text-amber-800">
                    {unreadNotifications} incidencia{unreadNotifications > 1 ? 's' : ''} pendiente{unreadNotifications > 1 ? 's' : ''} de presupuesto
                  </p>
                  <p className="text-sm text-amber-700">
                    Trabajadores han reportado incidencias que requieren presupuesto
                  </p>
                </div>
                <Button 
                  variant="outline" 
                  className="border-amber-400 text-amber-700 hover:bg-amber-100"
                  onClick={() => setActiveTab('incidents')}
                >
                  Ver incidencias
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Buscar por cliente o número..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[180px]">
              <Filter className="w-4 h-4 mr-2" />
              <SelectValue placeholder="Estado" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos los estados</SelectItem>
              <SelectItem value="draft">Borrador</SelectItem>
              <SelectItem value="sent">Enviado</SelectItem>
              <SelectItem value="accepted">Aceptado</SelectItem>
              <SelectItem value="rejected">Rechazado</SelectItem>
              <SelectItem value="executed">Ejecutado</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="quotes">
              Presupuestos ({quotes.length})
            </TabsTrigger>
            <TabsTrigger value="delivery">
              Albaranes ({deliveryNotes.length})
            </TabsTrigger>
            <TabsTrigger value="invoices">
              Facturas ({invoices.length})
            </TabsTrigger>
            <TabsTrigger value="incidents" className="relative">
              Incidencias
              {unreadNotifications > 0 && (
                <span className="ml-2 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                  {unreadNotifications}
                </span>
              )}
            </TabsTrigger>
          </TabsList>

          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-sky-600"></div>
            </div>
          ) : (
            <>
              <TabsContent value="quotes" className="space-y-3">
                {filterItems(quotes).length === 0 ? (
                  <Card>
                    <CardContent className="text-center py-12">
                      <FileText className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                      <p className="text-slate-500">No hay presupuestos</p>
                      <Button onClick={() => navigate('/quotes/new')} className="mt-4">
                        <Plus className="w-4 h-4 mr-2" />
                        Crear presupuesto
                      </Button>
                    </CardContent>
                  </Card>
                ) : (
                  filterItems(quotes).map(quote => (
                    <DocumentRow key={quote.id} item={quote} type="quote" />
                  ))
                )}
              </TabsContent>

              <TabsContent value="delivery" className="space-y-3">
                {filterItems(deliveryNotes).length === 0 ? (
                  <Card>
                    <CardContent className="text-center py-12">
                      <FileOutput className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                      <p className="text-slate-500">No hay albaranes</p>
                      <p className="text-sm text-slate-400 mt-2">
                        Los albaranes se crean desde presupuestos aceptados
                      </p>
                    </CardContent>
                  </Card>
                ) : (
                  filterItems(deliveryNotes).map(note => (
                    <DocumentRow key={note.id} item={note} type="delivery-note" />
                  ))
                )}
              </TabsContent>

              <TabsContent value="invoices" className="space-y-3">
                {filterItems(invoices).length === 0 ? (
                  <Card>
                    <CardContent className="text-center py-12">
                      <FileText className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                      <p className="text-slate-500">No hay facturas</p>
                      <p className="text-sm text-slate-400 mt-2">
                        Las facturas se crean desde presupuestos o albaranes
                      </p>
                    </CardContent>
                  </Card>
                ) : (
                  filterItems(invoices).map(invoice => (
                    <DocumentRow key={invoice.id} item={invoice} type="invoice" />
                  ))
                )}
              </TabsContent>

              <TabsContent value="incidents" className="space-y-3">
                {incidentNotifications.length === 0 ? (
                  <Card>
                    <CardContent className="text-center py-12">
                      <AlertTriangle className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                      <p className="text-slate-500">No hay incidencias pendientes</p>
                    </CardContent>
                  </Card>
                ) : (
                  incidentNotifications.map(notification => (
                    <Card key={notification.id} className={`${!notification.is_read ? 'border-amber-300 bg-amber-50' : ''}`}>
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <Badge variant="destructive">Incidencia</Badge>
                              {!notification.is_read && (
                                <Badge className="bg-amber-500">Nueva</Badge>
                              )}
                            </div>
                            <p className="font-medium">{notification.client_name}</p>
                            <p className="text-sm text-slate-500">
                              Reportado por: {notification.worker_name}
                            </p>
                            <p className="text-sm text-slate-500">
                              {notification.created_at && format(new Date(notification.created_at), "d MMM yyyy, HH:mm", { locale: es })}
                            </p>
                            {notification.observations && (
                              <p className="mt-2 text-sm bg-white p-2 rounded border">
                                {notification.observations}
                              </p>
                            )}
                          </div>
                          <div className="flex gap-2">
                            {notification.quote_id ? (
                              <Button 
                                variant="outline"
                                onClick={() => navigate(`/quotes/${notification.quote_id}`)}
                              >
                                Ver presupuesto
                              </Button>
                            ) : (
                              <Button 
                                onClick={() => handleCreateQuoteFromIncident(notification.id)}
                                className="bg-sky-600 hover:bg-sky-700"
                              >
                                <Plus className="w-4 h-4 mr-2" />
                                Crear presupuesto
                              </Button>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))
                )}
              </TabsContent>
            </>
          )}
        </Tabs>

        {/* Delete Dialog */}
        <AlertDialog open={deleteDialog.open} onOpenChange={(open) => !open && setDeleteDialog({ open: false, item: null, type: null })}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>¿Eliminar este documento?</AlertDialogTitle>
              <AlertDialogDescription>
                Esta acción no se puede deshacer.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancelar</AlertDialogCancel>
              <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
                Eliminar
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </OwnerLayout>
  );
};

export default Quotes;
